import React, { useState } from 'react';
import { 
  ShieldCheck, 
  Printer, 
  Smartphone, 
  CheckCircle2, 
  Lock, 
  User, 
  Landmark, 
  ShieldAlert, 
  Zap, 
  UploadCloud, // ✅ Changé ici (CloudUpload -> UploadCloud)
  ArrowRight,
  Database
} from 'lucide-react';

/**
 * 🧾 SAHELGUARD EMPIRE RECEIPT - V301.5.0
 * ALIGNÉ SUR LE MOTEUR FINANCIER (80/15/5) & SCELLAGE MATÉRIEL
 */
const EmpireReceipt = ({ data, onFinish }) => {
  const [backupStatus, setBackupStatus] = useState('idle');
  
  const nom_client = data.nom_client || `${data.profile?.userFirstName || ''} ${data.profile?.userLastName || ''}`.trim() || "CITOYEN SOUVERAIN";
  const imei = data.device_id || data.imei || data.profile?.imei1 || "UNITÉ SCELLÉE";
  const token_id = data.token || "SG-PROTOCOLE-SECURE";
  
  const receipt = data.receipt || {
    plan: data.profile?.gradeAmbition || "SOLO CITADELLE",
    period: "ANNUEL",
    quota: "32 GB CLOUD SECURE",
    total_paid: data.amount ? `${data.amount} FCFA` : "40,000 FCFA",
    devices: 1
  };

  const FIXED_FEE = 5000;

  const getGradeIcon = (plan) => {
    const p = plan?.toUpperCase() || "";
    if (p.includes('EMPIRE') || p.includes('GOUVERNEUR') || p.includes('GÉNÉRAL')) return <Landmark className="text-amber-500" size={32} />;
    if (p.includes('PRO') || p.includes('CITADELLE')) return <ShieldCheck className="text-emerald-500" size={32} />;
    return <Zap className="text-emerald-500" size={32} />;
  };

  const handleStartBackup = () => {
    setBackupStatus('uploading');
    setTimeout(() => setBackupStatus('finished'), 2500);
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          @page { margin: 0; size: auto; }
          body { background: white !important; margin: 0; padding: 0; }
          .no-print { display: none !important; }
          .print-container { 
            position: absolute; 
            top: 0; 
            left: 0;
            width: 100%; 
            border: none !important; 
            box-shadow: none !important; 
            border-radius: 0 !important;
          }
        }
        @keyframes progress {
          0% { width: 0%; }
          100% { width: 100%; }
        }
        .animate-progress { animation: progress 2.5s ease-in-out forwards; }
      `}} />

      <div className="fixed inset-0 bg-black/95 backdrop-blur-2xl z-[5000] flex items-center justify-center p-4 overflow-y-auto">
        <div className="print-container bg-white text-black w-full max-w-2xl rounded-[3rem] overflow-hidden shadow-[0_0_100px_rgba(0,0,0,0.5)] my-auto border border-slate-200 transition-all animate-in fade-in zoom-in duration-500">
          
          <div className="bg-slate-950 text-white p-10 flex justify-between items-center relative overflow-hidden">
            <div className="absolute top-0 right-0 opacity-10 rotate-12 pointer-events-none">
              <ShieldCheck size={220} />
            </div>
            
            <div className="relative z-10 flex items-center gap-6">
              <div className="p-4 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-md shadow-inner">
                {getGradeIcon(receipt.plan)}
              </div>
              <div>
                <p className="text-[10px] font-black text-emerald-400 uppercase tracking-[0.3em]">Licence Souveraine {receipt.period}</p>
                <h2 className="text-4xl font-black italic tracking-tighter uppercase leading-none mt-1">{receipt.plan}</h2>
              </div>
            </div>
            
            <div className="relative z-10 text-right">
              <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1">Authentification D1</p>
              <p className="text-sm font-mono font-bold text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-lg border border-emerald-500/20">{token_id.substring(0, 14)}</p>
            </div>
          </div>

          <div className="p-10 space-y-8">
            <div className="flex justify-between items-start border-b border-slate-100 pb-8">
              <div>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
                  <User size={14} className="text-slate-300"/> Détenteur Légal
                </p>
                <p className="text-2xl font-black uppercase tracking-tight text-slate-900">{nom_client}</p>
                <p className="text-[10px] font-bold text-slate-400 mt-1 italic">Vérifié par registre CNI/Passport</p>
              </div>
              <div className="text-right">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 flex items-center gap-2 justify-end">
                  <Smartphone size={14} className="text-slate-300"/> ID Matériel Scellé
                </p>
                <p className="text-xl font-mono font-bold text-slate-800 bg-slate-50 px-4 py-2 rounded-xl border border-slate-100">{imei}</p>
              </div>
            </div>

            <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100 relative">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Prestation Article 1</p>
                  <p className="text-md font-bold italic text-slate-700">Installation & Scellage Yarmotek</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-black text-slate-400">{FIXED_FEE.toLocaleString()} FCFA</p>
                </div>
              </div>
              
              <div className="pt-6 border-t border-slate-200 flex justify-between items-end">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 size={16} className="text-emerald-500" />
                    <p className="text-[11px] font-black text-emerald-600 uppercase tracking-widest">Statut : Payé & Scellé</p>
                  </div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Souveraineté SahelGuard Active</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-black text-slate-500 uppercase leading-none mb-1">Total Net Versé</p>
                  <p className="text-4xl font-black text-slate-950 tracking-tighter">{receipt.total_paid}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
               <div className="bg-emerald-50 border border-emerald-100 p-5 rounded-2xl flex items-center gap-4">
                  <div className="bg-emerald-600 text-white p-3 rounded-xl shadow-lg shadow-emerald-200">
                    <Database size={20} />
                  </div>
                  <div>
                    <h4 className="text-emerald-900 font-black text-[10px] uppercase tracking-tighter">Cloud R2 Scellé</h4>
                    <p className="text-emerald-700 text-xs font-bold">{receipt.quota}</p>
                  </div>
               </div>
               <div className="bg-slate-50 border border-slate-200 p-5 rounded-2xl flex items-center gap-4">
                  <div className="bg-slate-900 text-white p-3 rounded-xl shadow-lg">
                    <Lock size={20} />
                  </div>
                  <div>
                    <h4 className="text-slate-900 font-black text-[10px] uppercase tracking-tighter">Appareils Gérés</h4>
                    <p className="text-slate-700 text-xs font-bold">{receipt.devices} Unité(s)</p>
                  </div>
               </div>
            </div>

            <div className={`no-print rounded-3xl p-6 border-2 transition-all duration-700 ${
              backupStatus === 'finished' ? 'bg-emerald-50 border-emerald-500' : 'bg-slate-50 border-slate-200 shadow-inner'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                   <div className={`${backupStatus === 'finished' ? 'bg-emerald-500' : 'bg-slate-950'} text-white p-3 rounded-2xl transition-all duration-500 shadow-lg`}>
                    <UploadCloud size={22} /> {/* ✅ Changé ici aussi */}
                   </div>
                   <div>
                    <p className="text-[11px] font-black uppercase tracking-widest text-slate-900">Synchronisation Yarmotek-R2</p>
                    <p className="text-[10px] text-slate-500 mt-1 font-bold italic">Scellage définitif des métadonnées KYC en base D1</p>
                   </div>
                </div>
                
                {backupStatus === 'idle' && (
                  <button onClick={handleStartBackup} className="bg-slate-950 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase flex items-center gap-2 hover:bg-emerald-600 transition-all shadow-xl hover:scale-105 active:scale-95">
                    Démarrer <ArrowRight size={14}/>
                  </button>
                )}
                {backupStatus === 'uploading' && (
                  <div className="flex flex-col items-end gap-2">
                    <span className="text-[9px] font-black text-emerald-600 animate-pulse uppercase">Cryptage...</span>
                    <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 animate-progress"></div>
                    </div>
                  </div>
                )}
                {backupStatus === 'finished' && (
                  <div className="flex items-center gap-3 text-emerald-600 bg-white px-4 py-2 rounded-xl border border-emerald-200 shadow-sm">
                    <span className="text-[10px] font-black uppercase">SÉCURISÉ</span>
                    <CheckCircle2 size={24} />
                  </div>
                )}
              </div>
            </div>

            <div className="pt-8 border-t border-slate-100 flex justify-between items-end text-[10px]">
              <div className="font-mono text-slate-400 space-y-1">
                <p className="font-bold text-slate-500 uppercase tracking-widest">Certificat d'Intégrité :</p>
                <p>Généré : {new Date().toLocaleDateString('fr-FR')} • {new Date().toLocaleTimeString('fr-FR')}</p>
                <p>Unité de Certification : Yarmotek Infra-Sahel</p>
              </div>
              <div className="text-right">
                <div className="font-black italic uppercase text-slate-200 tracking-tighter text-4xl leading-none select-none">
                  SAHELGUARD
                </div>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.4em] mt-1">Empire of Africa Security</p>
              </div>
            </div>
          </div>

          <div className="p-8 bg-slate-50 flex gap-4 no-print border-t border-slate-200">
            <button 
              onClick={() => window.print()} 
              className="flex-1 bg-white border border-slate-300 hover:bg-slate-100 text-black py-5 rounded-2xl text-[11px] font-black uppercase flex items-center justify-center gap-3 transition-all shadow-sm hover:shadow-md"
            >
              <Printer size={18} /> Imprimer Reçu
            </button>
            <button 
              onClick={onFinish} 
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-5 rounded-2xl text-[11px] font-black uppercase flex items-center justify-center gap-3 shadow-xl transition-all hover:scale-[1.02] active:scale-95 border-b-4 border-emerald-800"
            >
              Terminer le Protocole <CheckCircle2 size={18} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmpireReceipt;